import { useState, useEffect } from "react";
import { auth, db } from "../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Get user role from Firestore
          const userDoc = await getDoc(doc(db, "users", firebaseUser.uid));
          console.log("User document exists:", userDoc.exists());
          console.log("User document data:", userDoc.data());

          if (!userDoc.exists()) {
            console.error(
              "User document not found in Firestore for UID:",
              firebaseUser.uid
            );
            console.log("Creating user document with default role 'User'");

            // Create user document with default role
            const defaultUserData = {
              email: firebaseUser.email,
              displayName: firebaseUser.displayName,
              role: "User",
              status: "Active",
              verificationLevel: "Verified",
              createdAt: new Date().toISOString().split("T")[0],
              created_by_name: firebaseUser.displayName || firebaseUser.email,
              lastLogin: "Never",
              agentCount: 0,
            };

            await setDoc(doc(db, "users", firebaseUser.uid), defaultUserData);

            setUser({
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: firebaseUser.displayName,
              ...defaultUserData,
            });
            setUserRole("User");
            return;
          }

          const userData = userDoc.data();
          console.log("Fetched user data:", userData);
          console.log("User role from Firestore:", userData?.role);

          // Check if role is missing or invalid
          if (
            !userData?.role ||
            !["User", "Moderator", "Admin"].includes(userData.role)
          ) {
            console.warn("Invalid or missing role, updating to 'User'");
            const updatedUserData = {
              ...userData,
              role: "User",
            };
            await setDoc(doc(db, "users", firebaseUser.uid), updatedUserData, {
              merge: true,
            });
            userData.role = "User";
          }

          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            ...userData,
          });

          const role = userData.role;
          console.log("Setting user role to:", role);
          setUserRole(role);
        } catch (error) {
          console.error("Error fetching user data:", error);
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            role: "User",
          });
          setUserRole("User");
        }
      } else {
        setUser(null);
        setUserRole(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const isAdmin = userRole === "Admin";
  const isModerator = userRole === "Moderator" || isAdmin;
  const isUser = userRole === "User" || isModerator;

  return {
    user,
    userRole,
    loading,
    isAdmin,
    isModerator,
    isUser,
  };
};
